import { Card, CardContent, Typography, Grid, Container, Box } from "@material-ui/core";
import { FC, useEffect, useState } from "react";
import { useLocalStorage } from "src/hooks/useLocalStorage";
import { LicenseDetails } from "./Types";
import { GetLicenseDetails, InsertUpdateLicenseForAppKey } from "./ConnectionService";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningAmberIcon from "@material-ui/icons/WarningAmber";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { Helmet } from "react-helmet-async";
import AlertMessage from "src/components/ui-components/AlertMessage";
import ImportFile from "src/components/ui-components/ImportFile";

const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  // Try native Date parsing first
  let date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }

  const parts = dateStr.split(/[-/]/); // split on "-" or "/"
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
      return new Date(year, month - 1, day); // month is 0-based
    }
  }

  return null;
};

const formatDate = (dateStr: string, addDays: number = 0): string => {
  const date = parseDate(dateStr);
  if (!date) return "Invalid Date";

  if (addDays > 0) {
    date.setDate(date.getDate() + addDays);
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getLicenseStatus = (endDate: string, graceDays: number) => {
  const today = new Date();
  const end = parseDate(endDate);
  if (!end) return { status: "invalid", icon: <ErrorOutlineIcon style={{ color: "gray" }} /> };

  const endPlusGrace = new Date(end);
  endPlusGrace.setDate(endPlusGrace.getDate() + graceDays);

  if (endPlusGrace < today) {
    return { status: "expired", icon: <ErrorOutlineIcon style={{ color: "red" }} /> };
  }
  if (end < today && today <= endPlusGrace) {
    return { status: "grace", icon: <WarningAmberIcon style={{ color: "orange" }} /> };
  }
  if ((end.getTime() - today.getTime()) / (1000 * 3600 * 24) > 7) {
    return { status: "valid", icon: <CheckCircleIcon style={{ color: "green" }} /> };
  }
  return { status: "nearExpiry", icon: <WarningAmberIcon style={{ color: "orange" }} /> };
};

const LicensePage: FC = () => {
  const { getItem } = useLocalStorage();
  const appKey = getItem("userDetails", "appKey");
  const [licenseDetails, setLicenseDetails] = useState<LicenseDetails | null>(null);

  const handleFileChangeAndUpload = async (selectedFile: File) => {
    try {
      const isSuccess = await InsertUpdateLicenseForAppKey(selectedFile, appKey);
      if (isSuccess) {
        AlertMessage({ msg: "License Updated Successfully", type: "success" });
        const updated = await GetLicenseDetails(appKey);
        setLicenseDetails(updated);
      } else {
        AlertMessage({ msg: "Failed to update license", type: "error" });
      }
    } catch (err) {
      console.error("Upload failed:", err);
      AlertMessage({ msg: "Failed to update license", type: "error" });
    }
  };

  useEffect(() => {
    const getAppLicenseDetails = async () => {
      try {
        const res = await GetLicenseDetails(appKey);
        setLicenseDetails(res);
      } catch (err) {
        console.error("Failed to fetch license details:", err);
        setLicenseDetails(null);
      }
    };

    if (appKey && appKey !== "") {
      getAppLicenseDetails();
    }
  }, [appKey]);

  if (!licenseDetails) {
    return (
      <Card sx={{ margin: "2%", padding: "1rem" }}>
        <Typography>No license details available.</Typography>
      </Card>
    );
  }

  const status = getLicenseStatus(licenseDetails.endDate, licenseDetails.graceDays);

  return (
   <>
     <Helmet>
       <title>License</title>
     </Helmet>

     <Box>
       <Container maxWidth="lg" sx={{ mt: 2 }}>
         <Grid container spacing={2}>
           <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
             <Card style={{ background: "#ffb3b3" }}>
               <CardContent style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                 <div>
                   <Typography variant="h6">Allowed Users</Typography>
                   <Typography variant="h5">{licenseDetails.userCount}</Typography>
                 </div>
               </CardContent>
             </Card>
           </Grid>

           <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
             <Card style={{ background: "#b3e5fc" }}>
               <CardContent style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                 <div>
                   <Typography variant="h6">Start Date</Typography>
                   <Typography variant="h5">{formatDate(licenseDetails.startDate)}</Typography>
                 </div>
               </CardContent>
             </Card>
           </Grid>

           <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
             <Card style={{ background: "#c8e6c9" }}>
               <CardContent style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                 <div>
                   <Typography variant="h6">Expiry Date</Typography>
                   <Typography variant="h5">
                     {formatDate(licenseDetails.endDate, licenseDetails.graceDays)}
                   </Typography>
                 </div>
                 {status.icon}
               </CardContent>
             </Card>
           </Grid>
         </Grid>

         {status.status === "expired" && (
           <Box mt={3}>
             <Card style={{ background: "#ffe6e6" }}>
               <CardContent>
                 <Typography variant="body1" color="error" align="center">
                   Your license is expired. Please contact your administrator.
                 </Typography>
               </CardContent>
             </Card>
           </Box>
         )}

        <Box mt={2} display="flex" flexDirection="column" alignItems="center">
          <ImportFile label="Upload License" onFileChange={handleFileChangeAndUpload} />
        </Box>
       </Container>
     </Box>
   </>
  );
};

export default LicensePage;
